// components/auth/ForgotPasswordCard.jsx
import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import LuxuryInput from '../ui/LuxuryInput';
import {AuthService} from "../../api/auth.api";

const ForgotPasswordCard = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) return;

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            await AuthService.forgotPassword(email);

            setSubmitted(true);
            setSuccess("Reset link sent successfully.");
        } catch (err) {
            setError(err.message || "Failed to send reset email");
        } finally {
            setLoading(false);
        }
    };

    return (<section className="py-16">
        <motion.div
            initial={{y: 40, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            transition={{duration: 0.6, ease: 'easeOut'}}
            className="max-w-md mx-auto px-4"
        >
            <div className="bg-white rounded-2xl shadow-soft p-8 sm:p-10">
                <AnimatePresence mode="wait">
                    {!submitted ? (<motion.div
                        key="form"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                    >
                        <h2 className="font-cormorant text-3xl text-charcoal mb-3 text-center">
                            Reset your password
                        </h2>

                        <p className="font-poppins text-charcoal/70 mb-8 text-center">
                            Enter your email and we’ll send you a secure reset link.
                        </p>
                        {error && (
                            <p className="text-sm text-red-500 text-center">{error}</p>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <LuxuryInput
                                type="email"
                                label="Email Address"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                size="large"
                            />

                            <motion.button
                                type="submit"
                                disabled={loading}
                                whileHover={{scale: loading ? 1 : 1.02}}
                                whileTap={{scale: loading ? 1 : 0.98}}
                                className={`
        w-full px-8 py-4 rounded-full
        bg-gradient-to-r from-champagne via-champagne/90 to-champagne/80
        text-charcoal font-medium text-lg
        hover:from-champagne/90 hover:via-champagne/80 hover:to-champagne/70
        transition-all duration-300
        shadow-lg hover:shadow-xl
        disabled:opacity-50 disabled:cursor-not-allowed
        relative overflow-hidden group
    `}
                            >
                                {/* Shimmer overlay */}
                                <span
                                    className="absolute inset-0 bg-white/30 opacity-0 group-hover:opacity-100
        transition-opacity duration-300"
                                />

                                <span className="relative flex items-center justify-center gap-2 font-poppins">
        {loading ? (<>
            <span className="w-5 h-5 border-2 border-charcoal/60 border-t-transparent rounded-full animate-spin"/>
            Sending…
        </>) : ('Send reset link')}
    </span>
                            </motion.button>

                        </form>

                        <div className="mt-8 pt-6 border-t border-charcoal/10 text-center">
                            <button
                                onClick={() => navigate('/login')}
                                className="font-poppins text-charcoal/70 hover:text-sage transition-colors"
                            >
                                ← Back to login
                            </button>
                        </div>
                    </motion.div>) : (<motion.div
                        key="success"
                        initial={{opacity: 0, scale: 0.95}}
                        animate={{opacity: 1, scale: 1}}
                        className="text-center py-6"
                    >
                        <div
                            className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg
                                className="w-8 h-8 text-sage"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>

                        <h3 className="font-cormorant text-2xl text-charcoal mb-3">
                            Check your email
                        </h3>

                        <p className="font-poppins text-charcoal/70 mb-6">
                            We sent a reset link to<br/>
                            <span className="font-semibold text-charcoal">{email}</span>
                        </p>

                        <div className="space-y-4">
                            <motion.button
                                whileHover={{scale: 1.02}}
                                whileTap={{scale: 0.98}}
                                onClick={() => setSubmitted(false)}
                                className="w-full py-3 rounded-xl bg-charcoal text-white font-poppins hover:bg-charcoal/90"
                            >
                                Try another email
                            </motion.button>

                            <button
                                onClick={() => navigate('/login')}
                                className="w-full font-poppins text-charcoal/70 hover:text-sage transition-colors"
                            >
                                Return to login
                            </button>
                        </div>
                    </motion.div>)}
                </AnimatePresence>
            </div>

            <p className="mt-8 text-center font-poppins text-sm text-charcoal/50">
                Need help?{' '}
                <a
                    href="mailto:support@kromaic.com"
                    className="text-sage hover:underline"
                >
                    support@kromaic.com
                </a>
            </p>
        </motion.div>
    </section>);
};

export default ForgotPasswordCard;
